import { useTranslations } from '@/i18n/use-translations';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useStore } from '@/providers/app-store-provider';
import { userPresList } from '@/api/account';
import useResponsive from '@/hooks/useResponsive';
import logger from '@/utils/client-logger';
import { Prescription } from '@/api/types/account';
import { handlerPrescriptionData } from '@/utils/prescription';
import SavedPrescriptionItem from './SavedPrescriptionItem';
import InfiniteScroll from '@/components/InfiniteScroll';
import ZeeloolCheckBox from '@/components/ZeeloolCheckBox';
import { EditOutlined } from '@zeelool/icon';
import { getApiPrescriptionType } from '@/app/_lens-components/utils';
import { EPrescriptionType } from '@/utils/enums/goods';
import useTrackEditPrescription from '../../services/useTrackEditPrescription';
import { CustomerPrescription } from '@/app/_lens-components/types';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import { usePrevious } from 'ahooks';
import { EDIT_PRESCRIPTION_SUB_STEPS } from '@/app/_lens-components/constants';
import { IS_FR } from '@/utils/constants';
import { uniqBy } from 'lodash-es';
import { useOpticalProcedureTitle } from '@/app/_lens-components/copywriting/useOpticalProcedureTitle';

interface SavedPrescriptionProps {
  prescriptionType: EPrescriptionType;
  /** 当前步骤是否激活（用于在步骤展示时可靠触发浏览埋点，避免 useInView 在 hidden 父节点下不触发） */
  isStepActive?: boolean;
  onEditPrescription: () => void;
  ref: React.RefObject<{
    selectedPres: CustomerPrescription | null;
  }>;
}

const SavedPrescription = ({
  prescriptionType,
  isStepActive = false,
  onEditPrescription,
  ref,
}: SavedPrescriptionProps) => {
  const t = useTranslations('lens-process');
  const choosePrescriptionTitle = useOpticalProcedureTitle(
    'my_prescription',
    t('choosePrescription'),
  );
  const presT = useTranslations('account.myPrescriptions');
  const { isMobile } = useResponsive();
  const token = useStore((state) => state.token);
  const { isPc } = useResponsive();
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  const { editPresStep, selectedPrescription } = useLensProcessStore();
  const [selectedPres, setSelectedPres] = useState<CustomerPrescription>(null);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const { trackSavedPrescriptionsView, trackClickSavedPrescription, trackEditSavedPrescription } =
    useTrackEditPrescription();
  const [loading, setLoading] = useState(false);
  const isFirstRef = useRef(true);
  const prevPresStep = usePrevious(editPresStep);

  const getPrescriptions = async (page = 1) => {
    try {
      setLoading(true);
      setIsGlobalLoading(true);
      const { data } = await userPresList({
        limit: 10,
        page,
        prescription_type: getApiPrescriptionType(prescriptionType),
        order: 'used_time',
      });

      if (data?.list?.data) {
        const arr = data.list.data.map((item) => {
          return handlerPrescriptionData(item);
        });

        setPrescriptions((prev) => uniqBy([...prev, ...arr], 'id'));
        setPagination((prev) => ({
          ...prev,
          total: data.list.total,
        }));
        if (isFirstRef.current) {
          setSelectedPres((current) => current || selectedPrescription || arr[0] || null);
        }
        // setPrescriptionTotal(response.data.list.total);
        if (isPc) {
          window.scrollTo({ top: 0 });
        }
        if (isFirstRef.current) {
          isFirstRef.current = false;
        }
      }
    } catch (error) {
      logger.error('获取处方列表失败', error);
    } finally {
      setLoading(false);
      setIsGlobalLoading(false);
    }
  };

  // 滚动到选中的处方
  const scrollToSelectedPrescription = () => {
    if (!selectedPres) return;

    // 使用 setTimeout 确保 DOM 已经渲染完成
    setTimeout(() => {
      // 查找选中的处方元素
      const selectedElement = document.querySelector(
        `[data-prescription-id="${selectedPrescription.id}"]`,
      );

      if (selectedElement) {
        // 滚动到选中的处方，使其在视口中居中
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      } else {
        // 如果没有找到具体的处方元素，滚动到处方列表区域
        const prescriptionSection = document.querySelector('[data-prescription-list]');
        if (prescriptionSection) {
          prescriptionSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }
      }
    }, 100); // 100ms 延迟确保 DOM 更新完成
  };
  useEffect(() => {
    if (!token) return;
    getPrescriptions(1);
  }, [token]);
  useEffect(() => {
    if (selectedPrescription) {
      setSelectedPres(selectedPrescription);
      setPrescriptions((prev) =>
        prev.map((item) =>
          item.id === selectedPrescription.id ? { ...item, ...selectedPrescription } : item,
        ),
      );
    }
  }, [selectedPrescription]);
  useEffect(() => {
    // * 只有从我的处方列表到编辑我的处方这个流程来回，才保留选中处方
    if (
      !(prevPresStep === EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM && isStepActive) &&
      !(
        editPresStep === EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM &&
        prevPresStep === EDIT_PRESCRIPTION_SUB_STEPS.CHOOSE_PRESCRIPTION
      )
    ) {
      setSelectedPres(prescriptions[0] || null);
    }
    if (prevPresStep === EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM && isStepActive) {
      scrollToSelectedPrescription();
    }
  }, [isStepActive, editPresStep]);
  const checkSelectedPrescription = (pres: Prescription) => {
    return selectedPres?.id === pres.id;
  };
  const titleWidthClassName = (() => {
    if (isMobile) return '';
    if (IS_FR) return 'max-w-[430px]';
    return 'max-w-[330px]';
  })();
  useImperativeHandle(ref, () => ({
    selectedPres,
  }));
  const genCustomHeader = (prescription) => {
    const { id, name, updated_at, prescription_type, prescription_type_name } = prescription;
    return (
      <>
        <div
          className={`w-full flex justify-between leading-[24px] ${isMobile ? 'items-start' : 'items-center'}`}
        >
          <div className={`flex gap-12 ${isMobile ? 'items-start' : 'items-center'}`}>
            <ZeeloolCheckBox
              checked={selectedPres?.id === id}
              sx={{
                '&.MuiCheckbox-root': {
                  padding: 0,
                  span: {
                    borderRadius: '50%',
                  },
                },
              }}
              className="f16"
            />
            <div className={`flex ${isMobile ? ' flex-col gap-6' : 'items-center gap-12'}`}>
              <span className={`f18 font-bs ${titleWidthClassName}`}>{prescription.name}</span>
              <div
                className={`flex items-center ${isMobile ? 'justify-end flex-row-reverse  gap-10' : 'flex-1 gap-12'}`}
              >
                <time className={`${isMobile ? 'text-12' : 'text-14'} font-br text-secondary-text`}>
                  {updated_at}
                </time>
                <span
                  className={`${isMobile ? 'text-12 leading-[12px] py-[3px]' : 'text-14 leading-[24px]'} font-br text-primary-purple bg-[#0006cf1a]  overflow-hidden px-8 rounded-12`}
                >
                  {getPrescriptionType(
                    prescription_type,
                    prescription_type_name || prescription_type,
                  )}
                </span>
              </div>
            </div>
          </div>
          {checkSelectedPrescription(prescription) && (
            <div
              className="flex items-center gap-4 cursor-pointer"
              onClick={(e) => {
                trackEditSavedPrescription(name);
                onEditPrescription();
                e.stopPropagation();
              }}
            >
              <EditOutlined className={`text-[#808080] ${isMobile ? '!text-14' : '!text-16'}`} />
              <span className={`${isMobile ? 'text-14' : 'text-16'} font-br text-primary-black`}>
                {presT('edit')}
              </span>
            </div>
          )}
        </div>
      </>
    );
  };
  const getPrescriptionType = (str: string, prescriptionTypeName: string) => {
    if (str === 'Sunglasses Prescription') {
      return presT('prescriptionTypes.sunglasses');
    }

    return prescriptionTypeName;
  };
  const handleReachBottom = () => {
    const next = pagination.page + 1;
    setPagination({ ...pagination, page: next });
    getPrescriptions(next);
  };
  // 步骤激活时触发浏览埋点（父级用 hidden/block 切换，useInView 在 hidden 下不可靠，改为依赖 isStepActive）
  useEffect(() => {
    if (isStepActive) {
      trackSavedPrescriptionsView();
    }
  }, [isStepActive]);
  return (
    <div className={`${isMobile ? 'px-14' : ''}`}>
      <div className={`xl:!pb-[0px] ${stepCommonClassName.stepTitle} !pl-0 xl:mb-[24px]`}>
        {choosePrescriptionTitle}
      </div>
      <section className={`flex flex-col gap-[16px]`} data-prescription-list>
        {prescriptions.map((prescription) => (
          <div key={prescription.id} data-prescription-id={prescription.id}>
            <SavedPrescriptionItem
              wrapperClassName={
                checkSelectedPrescription(prescription) ? 'border-primary-purple' : 'cursor-pointer'
              }
              isShowForm={selectedPres?.id === prescription.id}
              prescription={prescription}
              customHeader={() => genCustomHeader(prescription)}
              onSelectPrescription={() => {
                setSelectedPres(prescription);
                trackClickSavedPrescription({
                  save_prescription_name: prescription.name,
                  check_way: 'Checked',
                });
              }}
            />
          </div>
        ))}
      </section>
      <InfiniteScroll
        loading={loading}
        onReachBottom={handleReachBottom}
        page={pagination.page}
        total={pagination.total}
        limit={pagination.limit}
        showNoMore={false}
      />
    </div>
  );
};

export default SavedPrescription;
